import React, { useState, useEffect } from 'react';

function SimpleTimePicker({ value, onChange, placeholder = "시간 선택" }) {
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState('AM');

  // value가 변경되면 시간 파싱
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':').map(Number);
      
      if (h === 0) {
        setHour(12);
        setPeriod('AM');
      } else if (h === 12) {
        setHour(12);
        setPeriod('PM');
      } else if (h > 12) {
        setHour(h - 12);
        setPeriod('PM');
      } else {
        setHour(h);
        setPeriod('AM');
      }
      
      setMinute(m);
    }
  }, [value]);

  const updateTime = (newHour, newMinute, newPeriod) => {
    let h = newHour;
    
    if (newPeriod === 'AM') {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h += 12;
    }
    
    const formattedTime = `${String(h).padStart(2, '0')}:${String(newMinute).padStart(2, '0')}`;
    onChange(formattedTime);
  };

  const increaseHour = () => {
    const newHour = hour === 12 ? 1 : hour + 1;
    setHour(newHour);
    updateTime(newHour, minute, period);
  };

  const decreaseHour = () => {
    const newHour = hour === 1 ? 12 : hour - 1;
    setHour(newHour);
    updateTime(newHour, minute, period);
  };

  const increaseMinute = () => {
    const newMinute = minute >= 50 ? 0 : minute + 10;
    setMinute(newMinute);
    updateTime(hour, newMinute, period);
  };

  const decreaseMinute = () => {
    const newMinute = minute === 0 ? 50 : minute - 10;
    setMinute(newMinute);
    updateTime(hour, newMinute, period);
  };


  const setCurrentTime = () => {
    const now = new Date();
    const h = now.getHours();
    const m = Math.floor(now.getMinutes() / 10) * 10; // 10분 단위로 반올림
    
    if (h === 0) {
      setHour(12);
      setPeriod('AM');
    } else if (h === 12) {
      setHour(12);
      setPeriod('PM');
    } else if (h > 12) {
      setHour(h - 12);
      setPeriod('PM');
    } else {
      setHour(h);
      setPeriod('AM');
    }
    
    setMinute(m);
    
    const formattedTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    onChange(formattedTime);
  };

  return (
    <div className="flex-1 space-y-3">
      {/* 오전/오후 선택 버튼들 - 항상 표시 */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            setPeriod('AM');
            updateTime(hour, minute, 'AM');
          }}
          className={`flex-1 py-2 px-4 rounded-lg font-bold text-sm transition-all duration-200 ${
            period === 'AM' 
              ? 'bg-yellow-400 text-yellow-900 shadow-md' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          🌅 오전
        </button>
        <button
          type="button"
          onClick={() => {
            setPeriod('PM');
            updateTime(hour, minute, 'PM');
          }}
          className={`flex-1 py-2 px-4 rounded-lg font-bold text-sm transition-all duration-200 ${
            period === 'PM' 
              ? 'bg-purple-400 text-purple-900 shadow-md' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          🌙 오후
        </button>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between gap-4">

          {/* 시간 선택 */}
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={increaseHour}
              className="w-12 h-10 flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded-lg transition-all bg-blue-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
              </svg>
            </button>
            <div className="text-4xl font-bold text-gray-800 my-2 min-w-[50px] text-center">
              {hour}
            </div>
            <button
              type="button"
              onClick={decreaseHour}
              className="w-12 h-10 flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded-lg transition-all bg-blue-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className="text-xs text-gray-500 mt-1">시</div>
          </div>

          <div className="text-4xl font-bold text-gray-400">:</div>

          {/* 분 선택 */}
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={increaseMinute}
              className="w-12 h-10 flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded-lg transition-all bg-blue-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
              </svg>
            </button>
            <div className="text-4xl font-bold text-gray-800 my-2 min-w-[50px] text-center">
              {String(minute).padStart(2, '0')}
            </div>
            <button
              type="button"
              onClick={decreaseMinute}
              className="w-12 h-10 flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded-lg transition-all bg-blue-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className="text-xs text-gray-500 mt-1">분</div>
          </div>

          {/* 현재 시간 버튼 */}
          <button
            type="button"
            onClick={setCurrentTime}
            className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 font-bold text-sm"
          >
            지금
          </button>
        </div>
      </div>
    </div>
  );
}

export default SimpleTimePicker;